import React, { useState } from "react";
import api from "@/lib/api";
import { Globe, Users, ExternalLink, Copy } from "lucide-react";

/**
 * DiscoveryPortalPanel — standalone version extracted from
 * SalesPipelineTabsView.jsx so post-conversion surfaces (Franchisee
 * Detail) can reuse the exact same UI without any structural change
 * to the pipeline file.
 *
 * The component is intentionally a straight copy of the pipeline's
 * internal version, so the two are visually identical. Once the
 * pipeline codebase is comfortably refactored, it can `import` from
 * here too — for now the duplication is an acceptable trade-off to
 * keep the merge surface on ``SalesPipelineTabsView.jsx`` at zero.
 */
export default function DiscoveryPortalPanel({ contact }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [copyState, setCopyState] = useState("");
  const [plans, setPlans] = useState([]);
  const [franchisees, setFranchisees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState("");

  React.useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    (async () => {
      try {
        const { data } = await api.get(`/admin/contacts/${contact.id}/discovery-portal`);
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: e?.response?.data?.detail || "Failed to load" });
      }
    })();
    return () => { cancelled = true; };
  }, [contact.id]);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/territory-plans");
        setPlans(Array.isArray(data) ? data : (data?.plans || data?.items || []));
      } catch { /* leave empty */ }
    })();
    (async () => {
      try {
        const { data } = await api.get("/franchisees", { params: { limit: 500 } });
        const list = Array.isArray(data) ? data : (data?.franchisees || data?.items || []);
        const hasTag = (f, t) => Array.isArray(f.tags) ? f.tags.includes(t) : f.tags === t;
        setFranchisees(
          list.filter((f) => hasTag(f, "Franchisee") && !hasTag(f, "Demo") && !hasTag(f, "HQ")),
        );
      } catch { /* leave empty */ }
    })();
  }, []);

  const patch = async (fields) => {
    setSaving(true);
    try {
      await api.patch(`/admin/contacts/${contact.id}/discovery-portal`, fields);
      const { data: refreshed } = await api.get(`/admin/contacts/${contact.id}/discovery-portal`);
      setState({ loading: false, data: refreshed, error: null });
      setLastSaved(new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }));
    } catch (e) {
      setState((s) => ({ ...s, error: e?.response?.data?.detail || "Save failed" }));
    } finally {
      setSaving(false);
    }
  };

  const getLink = async () => {
    setCopyState("working");
    try {
      const { data } = await api.post(`/admin/contacts/${contact.id}/discovery-portal/ensure-token`);
      setState((s) => ({ ...s, data: { ...(s.data || {}), token: data.token, url: data.url } }));
      try {
        await navigator.clipboard.writeText(data.url);
        setCopyState("copied");
      } catch {
        setCopyState("ready");
      }
      setTimeout(() => setCopyState(""), 2500);
    } catch (e) {
      setCopyState("failed");
      setTimeout(() => setCopyState(""), 2500);
    }
  };

  const [previewState, setPreviewState] = useState("");
  const previewAsProspect = async () => {
    const win = window.open("about:blank", "_blank");
    setPreviewState("working");
    try {
      let url = state.data?.url;
      if (!url) {
        const { data } = await api.post(`/admin/contacts/${contact.id}/discovery-portal/ensure-token`);
        url = data.url;
        setState((s) => ({ ...s, data: { ...(s.data || {}), token: data.token, url: data.url } }));
      }
      if (win) {
        win.location = url;
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      setPreviewState("");
    } catch (e) {
      if (win) win.close();
      setPreviewState("failed");
      setTimeout(() => setPreviewState(""), 2500);
    }
  };

  if (state.loading) {
    return (
      <div className="rounded-xl border border-stone-200 bg-white p-5 text-sm text-stone-500">Loading Discovery Portal…</div>
    );
  }

  const d = state.data || {};
  const location = (d.shadow_day_location || "").toLowerCase();

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5" data-testid="discovery-panel">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-stone-500" />
          <div className="font-bold uppercase tracking-wider text-xs text-stone-800">Discovery Portal</div>
        </div>
        <div className="text-[10px] text-stone-500 tabular-nums">
          {saving ? "Saving…" : lastSaved ? `Saved ${lastSaved}` : ""}
        </div>
      </div>

      {state.error && (
        <div className="mb-3 text-xs text-red-600" data-testid="discovery-panel-error">{String(state.error)}</div>
      )}

      {d.token && !d.portal_active && (
        <div
          className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid="discovery-panel-access-paused"
        >
          <span className="inline-block w-2 h-2 mt-1 rounded-full bg-amber-500 shrink-0" />
          <div>
            <div className="font-bold uppercase tracking-wider">Access paused</div>
            <div className="mt-0.5">
              This contact is at <span className="font-semibold">{d.pipeline_status || "no stage"}</span> — the Discovery Portal is only visible while they&apos;re at <span className="font-semibold">Interested</span> or have been converted. Move them back to reactivate access on the same link.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1.5">
            Proposed Territory <span className="text-stone-400 font-normal normal-case">(defaults to the plan linked in Sales Pipeline)</span>
          </label>
          <select
            value={d.proposed_territory_plan_id || d.effective_territory_plan_id || ""}
            onChange={(e) => {
              const next = e.target.value || "";
              patch({ proposed_territory_plan_id: next || null });
            }}
            data-testid="discovery-territory-select"
            className="w-full text-sm border border-stone-300 rounded-lg px-3 py-2 bg-white"
          >
            <option value="">— Use linked plan from Sales Pipeline —</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>{p.name || p.centre_postcode || p.id.slice(0, 8)}</option>
            ))}
          </select>
          {d.territory ? (
            <div className="text-[11px] text-stone-500 mt-1.5">
              {d.territory_auto_linked ? (
                <span className="text-emerald-700 font-semibold">Auto-linked from Sales Pipeline · </span>
              ) : null}
              {d.territory.sector_codes?.length || 0} sector{(d.territory.sector_codes?.length || 0) === 1 ? "" : "s"}
              {d.territory.home_count ? ` · ${d.territory.home_count} homes` : ""}
            </div>
          ) : (
            <div className="text-[11px] text-stone-400 mt-1.5">Portal will show &ldquo;Awaiting Territory Generation&rdquo;</div>
          )}
        </div>

        <div className="lg:col-span-2">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1.5">Shadow Day</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input
              type="date"
              value={d.shadow_day_date ? String(d.shadow_day_date).slice(0, 10) : ""}
              onChange={(e) => patch({ shadow_day_date: e.target.value || null })}
              data-testid="discovery-shadow-date"
              className="text-sm border border-stone-300 rounded-lg px-3 py-2"
            />
            <select
              value={location || ""}
              onChange={(e) => patch({ shadow_day_location: e.target.value || null })}
              data-testid="discovery-shadow-location"
              className="text-sm border border-stone-300 rounded-lg px-3 py-2 bg-white"
            >
              <option value="">Location…</option>
              <option value="hq">Creative Mojo HQ (Sandra)</option>
              <option value="regional">Regional Franchisee</option>
            </select>
            {location === "regional" ? (
              <select
                value={d.shadow_day_regional_franchisee_id || ""}
                onChange={(e) => patch({ shadow_day_regional_franchisee_id: e.target.value || null })}
                data-testid="discovery-shadow-franchisee"
                className="text-sm border border-stone-300 rounded-lg px-3 py-2 bg-white"
              >
                <option value="">Choose franchisee…</option>
                {franchisees.map((f) => {
                  const label = [f.first_name, f.last_name].filter(Boolean).join(" ").trim() || f.organisation || f.email || f.id;
                  return <option key={f.id} value={f.id}>{label}</option>;
                })}
              </select>
            ) : (
              <div className="text-[11px] text-stone-400 self-center px-2">
                {location === "hq" ? "Sandra will be shown as host" : "Choose HQ or Regional"}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-stone-100" data-testid="discovery-flex-toggle-row">
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            className="mt-0.5 w-4 h-4 rounded border-stone-400 text-stone-900 focus:ring-stone-900"
            checked={!!d.show_flex_discovery}
            onChange={(e) => patch({ show_flex: e.target.checked })}
            data-testid="discovery-flex-toggle"
            disabled={saving}
          />
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-stone-900">
              Show Flex Documents in Discovery Portal
            </div>
            <div className="text-[11px] text-stone-500 mt-0.5">
              When enabled, the Main Franchise Agreement section shows two extra sample
              PDFs — Flex Payment Structure and Flex Addendum Agreement — pulled from
              <span className="font-mono text-[10px] bg-stone-100 px-1 py-0.5 rounded ml-1">All Files &gt; Admin &gt; Flex</span>.
              Independent from the Launch Portal Flex toggle. Default OFF.
            </div>
          </div>
        </label>
      </div>

      <div className="mt-5 pt-4 border-t border-stone-100 flex items-center justify-between gap-4 flex-wrap">
        <div className="text-[11px] text-stone-500 min-w-0 flex-1">
          {d.url ? (
            <span className="break-all"><Users className="w-3 h-3 inline mr-1 text-stone-400" /> {d.url}</span>
          ) : (
            <span className="text-stone-400">No link generated yet. Click the button to create one.</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={previewAsProspect}
            data-testid="discovery-preview-as-prospect"
            title="Open this contact's Discovery Portal in a new tab using their existing persistent token"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-stone-950 border border-stone-300 text-xs font-bold uppercase tracking-wider hover:bg-stone-50 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            {previewState === "working" ? "Opening…" : previewState === "failed" ? "Failed" : "Preview as Prospect"}
          </button>
          <button
            type="button"
            onClick={getLink}
            data-testid="discovery-ensure-link"
            title={state.data?.portal_generated_at ? "Portal already issued — click to copy the link again." : "Mint a persistent Discovery Portal link for this prospect and copy it to your clipboard."}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-stone-950 text-[#dddd16] text-xs font-bold uppercase tracking-wider hover:bg-stone-800 transition-colors"
          >
            <Copy className="w-3.5 h-3.5" />
            {copyState === "copied"
              ? "Copied ✓"
              : copyState === "working"
              ? "Working…"
              : copyState === "ready"
              ? "Ready — copy manually"
              : copyState === "failed"
              ? "Failed"
              : state.data?.portal_generated_at
              ? `Portal Link Generated ${(() => {
                  try {
                    const dt = new Date(state.data.portal_generated_at);
                    const dd = String(dt.getDate()).padStart(2, "0");
                    const mm = String(dt.getMonth() + 1).padStart(2, "0");
                    const yy = String(dt.getFullYear()).slice(-2);
                    return `${dd}/${mm}/${yy}`;
                  } catch { return ""; }
                })()}`
              : state.data?.token
              ? "Portal Link Generated"
              : "Provide Discovery Portal Link"}
          </button>
        </div>
      </div>
    </div>
  );
}
